var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import classNames from 'classnames';
import Portal from './Portal';
import { RCTooltipContext } from '../Tooltip';
import useKeyboardEventHandler from '../../hooks/useKeyboardEventHandler';
var STYLE_NAMESPACE = 'vcp-modal';
import { ModalContext } from './ModalContext';
import FocusTrap from 'focus-trap-react';
import { v4 as uuidv4 } from 'uuid';
import IconButton from '../IconButton';
import noop from '../../utils/noop';
import ModalNavigationButtons from './ModalNavigationButtons/ModalNavigationButtons';
var RCTooltipContextValue = {
    className: "".concat(STYLE_NAMESPACE, "__rc-tooltip"),
};
var focusTrapOptions = {
    tabbableOptions: { displayCheck: 'none' },
    allowOutsideClick: true,
    onDeactivate: noop,
    escapeDeactivates: false,
};
export var stopPropagation = function (e) { return e.stopPropagation(); };
var Modal = function (_a) {
    var _b;
    var visible = _a.visible, modalOverlayData = _a.modalOverlayData, modalWrapperData = _a.modalWrapperData, onClose = _a.onClose, children = _a.children, overlayZIndex = _a.overlayZIndex, _c = _a.type, type = _c === void 0 ? 'default' : _c, className = _a.className, _d = _a.modalRootId, modalRootId = _d === void 0 ? undefined : _d, closeButtonData = _a.closeButtonData, _e = _a.activeFocusTrap, activeFocusTrap = _e === void 0 ? true : _e, returnFocusRef = _a.returnFocusRef, navigationButtonsData = _a.navigationButtonsData, ariaLabel = _a.ariaLabel, _f = _a.useDeprecatedBodyScroll, useDeprecatedBodyScroll = _f === void 0 ? false : _f, dataAttributes = __rest(_a, ["visible", "modalOverlayData", "modalWrapperData", "onClose", "children", "overlayZIndex", "type", "className", "modalRootId", "closeButtonData", "activeFocusTrap", "returnFocusRef", "navigationButtonsData", "ariaLabel", "useDeprecatedBodyScroll"]);
    var modalId = React.useMemo(function () { return "".concat(STYLE_NAMESPACE, "-").concat(uuidv4()); }, []);
    var ModalContextValue = React.useMemo(function () { return ({ type: type, modalId: modalId, useDeprecatedBodyScroll: useDeprecatedBodyScroll }); }, [type, modalId, useDeprecatedBodyScroll]);
    var handleEscDown = useKeyboardEventHandler('Escape', onClose);
    var handleOverlayClick = React.useCallback(function (e) {
        if (onClose && e.target && e.target === e.currentTarget) {
            onClose();
        }
    }, [onClose]);
    React.useEffect(function () {
        if (activeFocusTrap) {
            focusTrapOptions.onDeactivate = function () {
                var _a;
                (_a = returnFocusRef === null || returnFocusRef === void 0 ? void 0 : returnFocusRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            };
        }
    }, [activeFocusTrap]);
    var modalStyle = React.useMemo(function () {
        return overlayZIndex === undefined
            ? modalOverlayData === null || modalOverlayData === void 0 ? void 0 : modalOverlayData.style
            : __assign({ zIndex: overlayZIndex }, modalOverlayData === null || modalOverlayData === void 0 ? void 0 : modalOverlayData.style);
    }, [overlayZIndex, modalOverlayData === null || modalOverlayData === void 0 ? void 0 : modalOverlayData.style]);
    return (React.createElement(RCTooltipContext.Provider, { value: RCTooltipContextValue },
        React.createElement(ModalContext.Provider, { value: ModalContextValue },
            React.createElement(Portal, { id: modalRootId },
                React.createElement(FocusTrap, { active: activeFocusTrap && visible, focusTrapOptions: focusTrapOptions },
                    React.createElement("div", __assign({}, modalOverlayData, { className: classNames("".concat(STYLE_NAMESPACE, "-backdrop"), modalOverlayData === null || modalOverlayData === void 0 ? void 0 : modalOverlayData.className, { visible: visible }), style: modalStyle, onKeyDown: handleEscDown, onMouseDown: handleOverlayClick }),
                        React.createElement("div", __assign({ onClick: stopPropagation, className: classNames(STYLE_NAMESPACE, "".concat(STYLE_NAMESPACE, "-").concat(type), className, { 'with-improved-scrolling-layout': !useDeprecatedBodyScroll }) }, modalWrapperData, { role: "dialog", tabIndex: closeButtonData ? -1 : 0, "aria-modal": visible, "aria-label": ariaLabel || modalId }, dataAttributes),
                            children,
                            navigationButtonsData && (React.createElement(ModalNavigationButtons, __assign({}, navigationButtonsData))),
                            closeButtonData && (React.createElement(IconButton, __assign({}, closeButtonData, { iconName: "close", className: classNames("".concat(STYLE_NAMESPACE, "-close-button"), (_b = {},
                                    _b["".concat(STYLE_NAMESPACE, "-close-button__external")] = closeButtonData.mode === 'floating',
                                    _b)) }))))))))));
};
export default Modal;
